import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Search, Edit, Trash2, ArrowLeft, X, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const AdminResearchers = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [researchers, setResearchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedResearcher, setSelectedResearcher] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchResearchers();
  }, [user, navigate, search]);

  const fetchResearchers = async () => {
    try {
      const url = search
        ? `${BACKEND_URL}/api/admin/researchers?search=${search}`
        : `${BACKEND_URL}/api/admin/researchers`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResearchers(data.researchers || []);
      }
    } catch (error) {
      console.error('Error fetching researchers:', error);
      toast({
        title: "Error",
        description: "Failed to fetch researchers",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (researcher) => {
    setSelectedResearcher(researcher);
    setEditForm({
      name: researcher.name || '',
      email: researcher.email || '',
      institution: researcher.institution || '',
      department: researcher.department || '',
      position: researcher.position || '',
      research_interests: researcher.research_interests || '',
      bio: researcher.bio || '',
      location: researcher.location || '',
      website: researcher.website || '',
      linkedin: researcher.linkedin || '',
      google_scholar: researcher.google_scholar || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/researchers/${selectedResearcher.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Researcher updated successfully"
        });
        setEditDialogOpen(false);
        fetchResearchers();
      } else {
        throw new Error('Failed to update researcher');
      }
    } catch (error) {
      console.error('Error updating researcher:', error);
      toast({
        title: "Error",
        description: "Failed to update researcher",
        variant: "destructive"
      });
    }
  };

  const deleteResearcher = async (researcherId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/researchers/${researcherId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Researcher profile deleted successfully"
        });
        setDeleteDialogOpen(false);
        setSelectedResearcher(null);
        fetchResearchers();
      } else {
        throw new Error('Failed to delete researcher');
      }
    } catch (error) {
      console.error('Error deleting researcher:', error);
      toast({
        title: "Error",
        description: "Failed to delete researcher",
        variant: "destructive"
      });
    }
  };

  const filteredResearchers = researchers;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold">Researcher Profiles Management</h1>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Search Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, institution, or research interests..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-gray-600">Total Researchers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{researchers.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Researchers Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Researcher Profiles</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading researchers...</div>
            ) : filteredResearchers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No researchers found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4 font-semibold">Name</th>
                      <th className="text-left p-4 font-semibold">Email</th>
                      <th className="text-left p-4 font-semibold">Institution</th>
                      <th className="text-left p-4 font-semibold">Research Interests</th>
                      <th className="text-left p-4 font-semibold">Created</th>
                      <th className="text-right p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResearchers.map((researcher) => (
                      <tr key={researcher.id} className="border-b hover:bg-gray-50">
                        <td className="p-4">{researcher.name}</td>
                        <td className="p-4">{researcher.email}</td>
                        <td className="p-4">{researcher.institution || 'N/A'}</td>
                        <td className="p-4 max-w-xs truncate">{researcher.research_interests || 'N/A'}</td>
                        <td className="p-4">
                          {new Date(researcher.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(researcher)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedResearcher(researcher);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Researcher Profile</DialogTitle>
            <DialogDescription>
              Update researcher information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="institution">Institution</Label>
                <Input
                  id="institution"
                  value={editForm.institution || ''}
                  onChange={(e) => setEditForm({...editForm, institution: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={editForm.department || ''}
                  onChange={(e) => setEditForm({...editForm, department: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={editForm.position || ''}
                  onChange={(e) => setEditForm({...editForm, position: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={editForm.location || ''}
                  onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="research_interests">Research Interests</Label>
              <Textarea
                id="research_interests"
                value={editForm.research_interests || ''}
                onChange={(e) => setEditForm({...editForm, research_interests: e.target.value})}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={editForm.bio || ''}
                onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                value={editForm.website || ''}
                onChange={(e) => setEditForm({...editForm, website: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn</Label>
                <Input
                  id="linkedin"
                  value={editForm.linkedin || ''}
                  onChange={(e) => setEditForm({...editForm, linkedin: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="google_scholar">Google Scholar</Label>
                <Input
                  id="google_scholar"
                  value={editForm.google_scholar || ''}
                  onChange={(e) => setEditForm({...editForm, google_scholar: e.target.value})}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Researcher Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedResearcher?.name}'s profile?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteResearcher(selectedResearcher?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminResearchers;
